import { useEffect } from 'react';
import Navbar from '../../components/Navbar/Navbar';
import Footer from '../../components/Footer/Footer';
import { Lock, Mail } from 'lucide-react';
import '../Policy.css';

export default function Privacy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="policy-page">
      <Navbar />
      <main className="policy-page__main">
        <header className="policy-header">
          <div className="policy-kicker">Privacy & Security</div>
          <h1 className="policy-title">Privacy Policy</h1>
          <p className="policy-subtitle">
            How Revitrone collects, protects, uses, and safeguards your personal data and architectural telemetry.
          </p>
          <div className="policy-meta">
            <Lock size={14} />
            <span>Last Updated: August 25, 2026</span>
          </div>
        </header>

        <div className="policy-card">
          <section className="policy-section">
            <h2>1. Introduction</h2>
            <p>
              Revitrone ("we", "our", or "us") is committed to maintaining the trust and confidence of our users. This Privacy Policy outlines how your personal information and technical data are collected, stored, processed, and protected when you use our website, software plugins, and managed AI proxy infrastructure.
            </p>
          </section>

          <section className="policy-section">
            <h2>2. Information We Collect</h2>
            <h3>A. Account & Profile Information</h3>
            <p>
              When you register for an account, we collect your name, email address, password hash, and subscription details. Authentication data is stored securely via our Supabase backend infrastructure.
            </p>

            <h3>B. Device & Licensing Telemetry</h3>
            <p>
              To enforce plan-specific device login limits (e.g., 1 device on Basic, 2 on Pro, 5 on Studio), our software plugin collects unique hardware and operating system fingerprints. This telemetry is strictly used for authentication and license enforcement.
            </p>

            <h3>C. Usage & API Proxy Data</h3>
            <p>
              When using our Managed API Proxy, we log operational metrics including total token counts, request timestamps, and response status codes to calculate subscription limits and monitor system reliability.
            </p>

            <h3>D. BYOK API Keys</h3>
            <p>
              For users operating under the Bring Your Own API Key (BYOK) model, your API keys are stored locally on your device or encrypted in transit strictly to route your prompts directly to your designated AI model vendor (e.g., OpenAI, Anthropic).
            </p>
          </section>

          <div className="policy-callout">
            <p>
              <strong>Data Ownership & AI Training Commitment:</strong> We do <strong>not</strong> sell your personal data or BIM project files to third parties. We do <strong>not</strong> use your private architectural models, drawings, or proprietary scripts to train public foundation models.
            </p>
          </div>

          <section className="policy-section">
            <h2>3. Payment Processing</h2>
            <p>
              All payment transactions are handled directly by our Merchant of Record, <strong>Paddle.com Market Ltd</strong>. Revitrone does not collect, store, or process full credit card numbers or raw bank details on our servers. Paddle manages compliance with PCI-DSS standards, tax withholding, and billing receipts.
            </p>
          </section>

          <section className="policy-section">
            <h2>4. Data Sharing & Processors</h2>
            <p>We share data only with essential infrastructure providers required to operate the Service:</p>
            <ul>
              <li><strong>Supabase:</strong> For cloud database hosting, encrypted auth storage, and session security.</li>
              <li><strong>Paddle:</strong> For subscription billing, transaction fulfillment, and customer support portal.</li>
              <li><strong>AI Model Vendors:</strong> Prompt text necessary to fulfill your requested design or code generation tasks is transmitted to model APIs (e.g., OpenAI/Anthropic) subject to their enterprise privacy terms.</li>
            </ul>
          </section>

          <section className="policy-section">
            <h2>5. Data Security & Storage</h2>
            <p>
              We implement industry-standard security measures including SSL/TLS encryption for all data in transit and AES-256 encryption for data at rest. Account security controls are regularly audited to prevent unauthorized access.
            </p>
          </section>

          <section className="policy-section">
            <h2>6. Your Data Rights</h2>
            <p>Depending on your jurisdiction (such as under GDPR or CCPA), you have the right to:</p>
            <ul>
              <li>Access and review the personal information we hold about you.</li>
              <li>Request correction of inaccurate account data.</li>
              <li>Request permanent deletion of your account and associated data.</li>
              <li>Export your script library and project history.</li>
            </ul>
          </section>

          <section className="policy-section">
            <h2>7. Updates to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. Any material changes will be reflected on this page with an updated revision date.
            </p>
          </section>

          <div className="policy-contact-box">
            <h3>Privacy Questions or Data Requests</h3>
            <p>To exercise your data protection rights or contact our privacy officer, please email us directly.</p>
            <a href="mailto:revitroneapp@gmail.com" className="policy-contact-btn">
              <Mail size={16} /> Contact Privacy Team
            </a>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
